import uploadToCDNRequest from '@picsart/cloud-storage/api/uploadToCDN';

import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { ErrorMessages } from '@/consts/errors';
import { getContext } from '@/context';
import { getAppStore, setAppStore } from '@/store';
import { ToastType } from '@/store/toast/types';
import getAccessToken from '@/utils/user/getAccessToken';
import sendUploadFailureAnalytics from '@/utils/analytics/sendUploadFailureAnalytics';

// The CDN upload endpoint's CORS allowlist only accepts credential headers.
// Forwarding the full `getHeaders()` set (lat-long, experiments, platform, …)
// trips the preflight and surfaces as a "Load failed" upload failure, so keep
// only the credential headers (casing preserved), falling back to dropping just
// Content-Type when no known credential header is present.
const UPLOAD_HEADER_ALLOWLIST = ['authorization', 'x-app-authorization'];

const pickUploadHeaders = (
  headers: Record<string, string>
): Record<string, string> => {
  const allowed = Object.entries(headers).filter(([key]) =>
    UPLOAD_HEADER_ALLOWLIST.includes(key.toLowerCase())
  );

  if (allowed.length === 0) {
    return Object.fromEntries(
      Object.entries(headers).filter(
        ([key]) => key.toLowerCase() !== 'content-type'
      )
    );
  }

  return Object.fromEntries(allowed);
};

const uploadFile = async (file: File) => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    // No token yet — e.g. the auth flag flipped just before the token
    // propagated after login. Throw so `uploadToCDN`'s catch refreshes the
    // token and retries, instead of silently failing the upload.
    throw new Error('Missing access token for CDN upload');
  }

  const { url } = await uploadToCDNRequest({
    file,
    accessToken,
    abortController: new AbortController(),
    headers: {
      ...pickUploadHeaders(getContext().utils.api.getHeaders() ?? {}),
      'x-touchpoint': MINIAPP_PACKAGE_ID,
    },
  });

  return url;
};

const uploadToCDN = async (file: File) => {
  try {
    return await uploadFile(file);
  } catch {
    try {
      setAppStore({ isGenerating: true });
      await getContext().handlers.refreshToken();
      return await uploadFile(file);
    } catch (error) {
      getAppStore().showToast(ErrorMessages.uploadToCDN, ToastType.Error);
      sendUploadFailureAnalytics(
        error instanceof Error ? error.message : ErrorMessages.uploadToCDN
      );
      return null;
    } finally {
      setAppStore({ isGenerating: false });
    }
  }
};

export default uploadToCDN;
